import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { brandInfo, navLinks } from '@/mocks/site'

export default function Navbar() {
  const { t } = useTranslation()
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])

  const solid = scrolled || menuOpen

  const isActive = (to: string) => (to === '/' ? location.pathname === '/' : location.pathname.startsWith(to))

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        solid
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <nav className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-3 cursor-pointer shrink-0">
            <span
              className={`w-11 h-11 md:w-12 md:h-12 rounded-full overflow-hidden ring-1 transition-all shrink-0 ${
                solid ? 'ring-primary-400' : 'ring-primary-300/70'
              }`}
            >
              <img
                src={brandInfo.logoImage}
                alt="Shabi Media logo"
                title="Shabi Media — photography, videography and creative design since 1988"
                className="w-full h-full object-cover"
              />
            </span>
            <span className="leading-none hidden sm:block">
              <span
                className={`block font-heading text-lg md:text-2xl font-semibold tracking-wide transition-colors ${
                  solid ? 'text-foreground-900' : 'text-background-50'
                }`}
              >
                {brandInfo.name}
              </span>
              <span
                className={`block text-[9px] md:text-[11px] tracking-[0.3em] uppercase mt-1 transition-colors ${
                  solid ? 'text-primary-600' : 'text-primary-300'
                }`}
              >
                {t('hero.badgeSince')}
              </span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-5 xl:gap-7">
            {navLinks.map((link) => {
              const active = isActive(link.to)
              return (
                <Link
                  key={link.key}
                  to={link.to}
                  className={`text-sm font-medium tracking-wide cursor-pointer transition-colors whitespace-nowrap ${
                    solid
                      ? active
                        ? 'text-primary-600'
                        : 'text-foreground-700 hover:text-primary-600'
                      : active
                        ? 'text-primary-300'
                        : 'text-background-100 hover:text-primary-300'
                  }`}
                >
                  {t(`nav.${link.key}`)}
                </Link>
              )
            })}
          </div>

          <div className="flex items-center gap-3">
            <a
              href={brandInfo.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium tracking-wide cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-base" />
              {t('common.bookShoot')}
            </a>
            <button
              type="button"
              aria-label="Toggle menu"
              onClick={() => setMenuOpen((v) => !v)}
              className={`lg:hidden w-11 h-11 flex items-center justify-center rounded-md border cursor-pointer transition-colors ${
                solid
                  ? 'border-background-300 text-foreground-800'
                  : 'border-background-100/40 text-background-50'
              }`}
            >
              <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="lg:hidden pb-5 pt-2 border-t border-background-200">
            <div className="flex flex-col">
              {navLinks.map((link) => (
                <Link
                  key={link.key}
                  to={link.to}
                  onClick={() => setMenuOpen(false)}
                  className={`py-3 text-sm font-medium cursor-pointer border-b border-background-100 last:border-0 transition-colors ${
                    isActive(link.to) ? 'text-primary-600' : 'text-foreground-700 hover:text-primary-600'
                  }`}
                >
                  {t(`nav.${link.key}`)}
                </Link>
              ))}
              <a
                href={brandInfo.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMenuOpen(false)}
                className="mt-4 inline-flex items-center justify-center gap-2 h-12 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors"
              >
                <i className="ri-whatsapp-line text-base" />
                {t('common.bookShootWhatsapp')}
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  )
}